<template>
  <div>
    <!-- `target`: a janela nasce do card ou da linha clicada, e fecha de volta para ele -->
    <ontos-dialog
        :model-value="true"
        :fullscreen="$vuetify.display.smAndDown"
        max-width="1100px"
        :target="openedFrom || undefined"
        @update:model-value="onDialogInput"
    >
      <!-- v-show e não v-if: entrar na edição não pode DESTRUIR a ficha. O load() dela roda no
           mounted, então destruí-la faria voltar custar uma releitura — e voltar sem ter mudado
           nada não mudou nada. Viva e escondida, voltar é instantâneo. -->
      <!-- v-if no item e v-show no modo: criando ainda não há instância, e montar a ficha sem
           id a faria buscar o nada. Depois de salvar o id chega, ela monta escondida, carrega, e
           só então a janela troca — a mesma emenda de progresso da edição. -->
      <!-- E não monta enquanto a janela abriu JÁ editando com o que o quadro tinha (`deferView`): a
           ficha leria do servidor o que ninguém está vendo. Monta ao voltar ou ao salvar. -->
      <v-card v-if="current.instance && !deferView" v-show="mode === 'view'">
        <ontos-dialog-loading v-if="!contentLoaded"/>
        <!-- v-show e não v-if: sem montar, a ficha nunca carregaria. O display:none
             na raiz dela também tira da tela a barra fininha, que aqui é redundante. -->
        <ontos-instance-view
            ref="view"
            v-show="contentLoaded"
            :key="contentKey"
            :context="current"
            :workspace="workspace"
            :preview="lead"
            @loaded="onViewLoaded"
            @edit="onEdit"
            @open-instance="onOpenInstance"
            @changed="onChildChanged"
            @deleted="onDeleted()"
            @not-found="$emit('closed')"
            @showMessage="$emit('showMessage', $event)"
            @showErrorMessage="onContentError($event)"
        >
          <template #controls>
            <ontos-dialog-actions :on-full-page="expandView" @closed="$emit('closed')"/>
          </template>
        </ontos-instance-view>
      </v-card>

      <!-- Editar TROCA o que esta janela mostra, em vez de abrir outra por cima: é o mesmo
           v-dialog, então o fundo não escurece de novo e ESC continua fechando uma janela só.
           Sem v-card em volta — o painel traz o dele, e o overlay de salvar precisa ser irmão
           dele (ver o FormPanel).

           O editando vem do evento da ficha, com a cópia editável e os candidatos que ela já
           tinha carregado: a janela não relê nada para mostrar o formulário. -->
      <ontos-instance-edit v-if="mode === 'edit' && editing"
                           v-bind="editing"
                           @saved="onSaved"
                           @showMessage="$emit('showMessage', $event)"
                           @showErrorMessage="$emit('showErrorMessage', $event)">
        <template #controls="{dirty, draft, original}">
          <ontos-dialog-actions back :dirty="dirty" :on-full-page="() => expandEdit(draft, original)"
                                @closed="onEditLeft"/>
        </template>
      </ontos-instance-edit>

      <!-- Criar na MESMA janela: salvar troca para a ficha do item recém-criado, e o que estava
           atrás — a faixa do Kanban, a listagem — segue intocado.

           Não é esta a janela que o "+" de um campo de relacionamento abre: lá o item criado
           precisa VOLTAR para ser vinculado, e quem faz isso é o CreateInstanceDialog. -->
      <ontos-instance-create v-if="mode === 'create'"
                             :context="context"
                             v-bind="creating || {}"
                             @saved="onCreated"
                             @showMessage="$emit('showMessage', $event)"
                             @showErrorMessage="$emit('showErrorMessage', $event)">
        <template #controls="{dirty, draft}">
          <ontos-dialog-actions :dirty="dirty" :on-full-page="() => expandCreate(draft)"
                                @closed="$emit('closed')"/>
        </template>
      </ontos-instance-create>

      <!-- UM progresso só, do clique em Salvar até a ficha estar pronta.
           Antes eram dois em sequência, com um piscar entre eles: o cinza do gravar sumia, a
           janela trocava de conteúdo e a ficha recomeçava do zero com o seu próprio indicador.
           Aqui o cinza do gravar emenda no da releitura e só sai quando há o que mostrar. -->
      <ontos-busy-overlay :model="finishing"/>

      <!--
        Só os pedidos marcados com `over` chegam aqui: o card do Kanban de
        relacionamento e o link de quem impede a exclusão. Link da própria ficha troca
        esta janela, sem empilhar nada.

        Precisa ficar DENTRO do slot do v-dialog acima, não como irmão dele: é por
        provide/inject que o Vuetify descobre os aninhados. Fora daqui, o modal de
        baixo se acharia o de cima e sequestraria o foco deste.
      -->
      <ontos-view-instance-dialog
          v-if="child"
          :context="child"
          :preview="childPreview"
          :workspace="workspace"
          @changed="onChildChanged"
          @deleted="onChildDeleted"
          @closed="child = null"
          @showMessage="$emit('showMessage', $event)"
          @showErrorMessage="$emit('showErrorMessage', $event)"
      />
    </ontos-dialog>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from "vue-facing-decorator";
import InstanceView from "@/components/live/instance/InstanceView.vue";
import InstanceCreate from "@/components/live/instance/InstanceCreate.vue";
import InstanceEdit from "@/components/live/instance/InstanceEdit.vue";
import BusyOverlay from "@/components/shared/BusyOverlay.vue";
import DialogActions from "@/components/shared/DialogActions.vue";
import DialogLoading from "@/components/shared/DialogLoading.vue";
import {InstanceContext} from "@/types/InstanceContext"
import {handOver} from "@/utils/handover"
import {createDraftKey, createRoute} from "@/utils/instanceCreation";
import {editingFrom, Preview} from "@/utils/instancePreview";
import {EnhancedWorkpace} from "@/types/Workspace";
import {
  instanceContentKey, instanceKey, instanceRoute, instanceViewKey, isInPlace, takeEditing
} from "@/utils/instanceLink";
import {takeOrigin} from "@/utils/motion";

// O name permite usar a própria tag no template acima sem importar a si mesmo
// (o componente fica registrado sob o próprio nome), evitando o ciclo.
@Component({emits: ['changed', 'created', 'deleted', 'closed', 'showMessage', 'showErrorMessage'],
  name: 'ontos-view-instance-dialog',
  components: {
    'ontos-instance-view': InstanceView,
    'ontos-instance-create': InstanceCreate,
    'ontos-instance-edit': InstanceEdit,
    'ontos-busy-overlay': BusyOverlay,
    'ontos-dialog-actions': DialogActions,
    'ontos-dialog-loading': DialogLoading
  }
})
class ViewInstanceDialog extends Vue {
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  /**
   * Abrir já criando, em vez de mostrando uma ficha. O `context` então tem a coleção e não tem
   * item — ele aparece ao salvar, e aí a janela vira a ficha dele.
   *
   * Objeto, e não booleano: o que vem dentro são as props do formulário de criar — os valores
   * com que o item nasce e, quando quem abriu já os leu, os candidatos dos relacionamentos. É o
   * mesmo arranjo do `editing`, pela mesma razão: a janela repassa, não interpreta.
   */
  @Prop({type: Object, default: null}) readonly creating!: any
  /**
   * O que quem abriu a janela já tem do item — a linha da lista, do Kanban ou de uma tabela de
   * relacionamento. A ficha o mostra já e relê por trás (ver `InstanceView.load`).
   */
  @Prop({type: Object, default: null}) readonly preview!: Preview | null

  /**
   * A prévia que a PRÓXIMA ficha monta já mostrando: a que veio de fora, ou a de um salvar. Vale uma
   * montagem — a ficha a leva e a janela a solta (`onViewLoaded`), senão uma remontagem depois (a de
   * um filho que mudou) voltaria a mostrar o que já envelheceu.
   */
  lead: Preview | null = null
  /** A que vai com a ficha aberta por cima (`child`). */
  childPreview: Preview | null = null

  /**
   * O que esta janela está mostrando: a ficha ou o formulário de edição.
   *
   * Uma janela, vários conteúdos — é o que separa "editar" de "abrir outra janela". Trocar o
   * modo não mexe no `v-dialog`, então não há segunda camada nem segundo ESC.
   */
  mode: 'view' | 'edit' | 'create' = 'view'
  /** O que o editor precisa, como a ficha o entregou. Null fora do modo de edição. */
  editing: any = null
  /**
   * Gravou, e agora espera a ficha ficar pronta para aparecer.
   *
   * É o que emenda os dois tempos num progresso só: a janela segue no formulário, coberta, e a
   * ficha recarrega atrás. Trocar o conteúdo antes mostraria o indicador de carga dela, que é o
   * segundo giro que incomoda.
   */
  finishing = false
  /** Ficha aberta POR CIMA desta, e não no lugar dela; ver o template. */
  child: InstanceContext | null = null
  /**
   * Instância para a qual se navegou por dentro do modal, ou null enquanto é a que o
   * host pediu. Navegar troca o conteúdo desta janela em vez de abrir outra: cada
   * nível empilhado escurecia mais o fundo, e sair de uma cadeia de três exigia três
   * ESC. Sem trilha de volta — fechar fecha.
   */
  replaced: InstanceContext | null = null
  reloadNonce = 0
  contentLoaded = false
  /** O card ou a linha que abriu esta janela, quando foi um clique — ver o `markOrigin`. */
  openedFrom: Element | null = null

  /** A instância que está na tela. Criando, ela ainda não tem id — até salvar. */
  get current(): InstanceContext {
    return this.replaced || this.context
  }

  /**
   * Criou: a janela passa a ser a ficha do item novo.
   *
   * Pelo mesmo caminho da edição — o item vira o `replaced`, a ficha monta escondida, carrega, e
   * o watch do `contentLoaded` revela. Um progresso só, do Salvar até a ficha pronta.
   */
  onCreated(added: any, preview?: Preview) {
    this.lead = preview || null
    this.finishing = true
    this.replaced = {...this.context, instance: added._instance}
    this.$emit('created', added)
    this.$emit('changed')
  }

  /**
   * O modo inicial sai da prop, e aqui — não no campo.
   *
   * Campo de classe é inicializado antes de as props existirem: `this` ali é a instância crua,
   * e `this.creating` sairia `undefined`. A janela nascia em `view` sem item nenhum e abria
   * vazia. É a mesma pedra que o `countTitle` do tabuleiro documenta.
   */
  created() {
    if (this.creating) this.mode = 'create'
    this.startsEditing = !this.creating && takeEditing()
    //Abrindo para editar com o que o quadro leu — o item e os candidatos —, a edição abre já, sem ir ao
    //servidor (relato do usuário: carregava o que o quadro já tinha)
    const editing = this.startsEditing && this.preview && editingFrom(this.current, this.preview, this.workspace)
    if (editing) {
      this.startsEditing = false
      this.deferView = true
      this.onEdit(editing)
    }
    //Abrindo para editar sem os candidatos, sem a prévia: o formulário precisa da leitura COMPLETA (a
    //edição recebe o item e os candidatos da ficha), e com prévia o `loaded` chegaria antes dela
    this.lead = this.startsEditing ? null : this.preview
    this.openedFrom = takeOrigin()
  }

  /** Abriu pelo lápis do card sem os candidatos: assim que a ficha carregar, a janela troca para a edição. */
  startsEditing = false
  /**
   * Abriu já editando, com o que o quadro tinha, e a ficha ainda não foi montada. Ela monta ao voltar ou
   * ao salvar, já mostrando a prévia — e relê só então o que falta, como no clique no card.
   */
  deferView = false

  /**
   * O host trocou a instância desta janela por fora: a navegação de dentro não vale mais.
   *
   * Pelo ITEM (`instanceKey`), e não pelo objeto: o casco passa o contexto escrito no template
   * (`{system, idea}`), um objeto novo a cada vez que ele redesenha. Logo depois de criar, o "Salvo"
   * redesenha o casco — e a janela esquecia o item recém-criado, a ficha não montava e o cinza do
   * salvar ficava para sempre (relato do usuário).
   */
  @Watch('context')
  onContextChanged(context: InstanceContext, before?: InstanceContext) {
    if (before && instanceKey(context) === instanceKey(before)) return
    this.replaced = null
  }

  @Watch('preview')
  onPreviewChanged() {
    this.lead = this.preview
  }

  /** A ficha já está na tela — a prévia, ou a leitura completa: a prévia da vez foi usada. */
  onViewLoaded() {
    this.contentLoaded = true
    this.lead = null
    //Pelo mesmo caminho do lápis da ficha: ela entrega o que a edição precisa (`edit`), e a janela
    //troca de modo no mesmo quadro — a ficha não chega a aparecer
    if (this.startsEditing) {
      this.startsEditing = false
      ;(this.$refs.view as any)?.onEdit()
    }
  }

  /** Trocar o contentKey remonta a ficha, então a carga recomeça do zero. */
  @Watch('contentKey')
  onContentKeyChanged() {
    this.contentLoaded = false
  }

  onOpenInstance(context: InstanceContext, over = false, preview?: Preview) {
    if (over) {
      this.child = context
      this.childPreview = preview || null
    } else this.replaced = context
  }

  /** A ficha pediu para editar: esta janela troca de conteúdo. */
  onEdit(editing: any) {
    this.editing = editing
    this.mode = 'edit'
  }

  /**
   * Desistiu da edição: volta para a ficha e NÃO relê.
   *
   * Nada mudou — o editor trabalha sobre uma cópia profunda, então o que a ficha mostra nunca
   * foi tocado — e ela continuou montada atrás. Reler aqui seria uma ida ao servidor para
   * confirmar que nada aconteceu.
   */
  onEditLeft() {
    this.mode = 'view'
    this.editing = null
    this.deferView = false
  }

  /**
   * Salvou: o que está na tela envelheceu, então a ficha é remontada pelo nonce — mas a janela
   * só troca quando ela estiver pronta. Quem faz a troca é o watch do `contentLoaded`.
   *
   * O item que quem salvou deixou de ver (confirmado no `AccessLossDialog`) não tem ficha a
   * remontar: a janela fecha, e quem hospeda relê pelo `changed`.
   */
  onSaved(_instance: unknown, {hidden, preview}: {hidden?: boolean, preview?: Preview} = {}) {
    if (hidden) {
      this.$emit('changed', this.current.instance)
      this.$emit('closed')
      return
    }
    //O que se acabou de salvar, já na ficha que monta: ela revela na hora e relê por trás
    this.lead = preview || null
    this.finishing = true
    this.reloadNonce++
    this.deferView = false
    this.$emit('changed', this.current.instance)
  }

  /**
   * A ficha excluiu o item que estava na tela.
   *
   * Avisa **com o id**, e num evento próprio: assim quem hospeda pode tirar aquele card do
   * quadro sem reler a coleção inteira. Era um `changed` seco, e "mudou alguma coisa" obriga
   * quem ouve a reler tudo para descobrir o quê.
   *
   * O item é o CORRENTE, que pode não ser o que abriu a janela: ela navega por dentro.
   */
  onDeleted() {
    //A coleção junto: quem tira o item de uma lista que mistura coleções precisa dela (ver o
    //`removeDeleted` da ficha), e os ids são de cada coleção
    this.$emit('deleted', this.current.instance, this.current.idea)
    this.$emit('closed')
  }

  /**
   * O filho excluiu o item dele: sai das listas desta ficha, sem reler (ver o `removeDeleted`). A
   * ficha avisa a mudança já aplicada (`changed` com `IN_PLACE`), e o `onChildChanged` só a repassa.
   */
  onChildDeleted(id: number | string, idea: string) {
    (this.$refs.view as any)?.removeDeleted(idea, id)
  }

  /**
   * A ficha recarregada ficou pronta: agora sim a janela volta a mostrá-la.
   *
   * Vale também quando a releitura FALHA, porque o `onContentError` também libera o
   * `contentLoaded` — senão um erro deixaria a janela presa no cinza para sempre.
   */
  @Watch('contentLoaded')
  onContentLoadedChanged() {
    if (!this.finishing || !this.contentLoaded) return
    this.finishing = false
    this.onEditLeft()
    this.mode = 'view'
  }

  /**
   * Expandir a ficha: a MESMA ficha na página, sem ir ao servidor — como a edição, logo abaixo
   * (pedido do usuário). O que a página leria — o item, os candidatos dos relacionamentos, os
   * metadados dos reversos — é o que a ficha daqui já leu (`asRead`). Sem leitura que deu certo — ainda
   * carregando, ou com erro —, não há o que levar, e a página lê por conta própria. O `loaded` da ficha,
   * e não o `contentLoaded` da janela: este fica verdadeiro também no erro, para soltar o carregando.
   *
   * Expandindo com a ficha ainda na prévia, a página recebe a prévia — não uma leitura completa —, e a
   * leitura que faltava continua lá (ver `InstanceView.asRead`).
   */
  expandView() {
    const view = this.$refs.view as InstanceType<typeof InstanceView> | undefined
    if (view && view.loaded) handOver(instanceViewKey(this.current), view.asRead())
    this.$router.push(this.fullPageRoute).catch(() => undefined)
  }

  /**
   * Expandir a edição: a MESMA edição na página, continuando de onde parou.
   *
   * Tudo o que a rota leria já está aqui — o rascunho, os candidatos, os metadados dos reversos
   * —, e vai junto pela entrega. Antes ela relia do servidor e o rascunho se perdia, o que
   * obrigava a avisar antes de sair; levando-o, não há perda e não há o que avisar.
   *
   * E o original junto: é contra ele que a página mede o rascunho (ver a prop `original` do
   * InstanceEdit). Só o rascunho, e a página o tomava por ponto de partida.
   */
  expandEdit(draft: any, original: any) {
    const {system, idea, instance} = this.current
    handOver(instanceKey(this.current), {...this.editing, instance: draft, original})
    this.$router.push({name: 'edit-instance', params: {system, idea, instance: String(instance)}})
        .catch(() => undefined)
  }

  /**
   * Expandir a criação, pelo mesmo caminho — só a chave difere, porque ainda não há item.
   *
   * O `known` traz a coleção, os candidatos e os reversos; o rascunho é o que foi digitado.
   */
  expandCreate(draft: any) {
    handOver(createDraftKey(this.context), {...(this.creating?.known || {}), instance: draft})
    this.$router.push(createRoute(this.context.system, this.context.idea)).catch(() => undefined)
  }

  /**
   * Se o load() estourar, o `loaded` nunca é emitido e o spinner giraria para sempre.
   * Melhor liberar a janela e deixar o erro aparecer na barra de mensagens.
   */
  onContentError(message: any) {
    this.contentLoaded = true
    this.$emit('showErrorMessage', message)
  }

  // load() só roda no mounted da folha, então remontar por :key é o que recarrega
  get contentKey() {
    return instanceContentKey(this.current, this.reloadNonce)
  }

  /**
   * Mudança feita num nível abaixo precisa aparecer aqui e continuar subindo.
   *
   * Sobe o id do item DESTA janela, e não o do filho: o filho costuma ser de outra coleção, e
   * quem ouve lá em cima mostra esta. O que mudou nele e respinga aqui — um vínculo reverso, por
   * exemplo — chega pela releitura que o `reloadNonce` dispara.
   *
   * A não ser que a ficha já tenha aplicado a mudança em si mesma (`IN_PLACE`): aí só se repassa.
   */
  onChildChanged(change?: unknown) {
    if (!isInPlace(change)) this.reloadNonce++
    this.$emit('changed', this.current.instance)
  }

  onDialogInput(open: boolean) {
    if (!open) this.$emit('closed')
  }

  //A instância que está na tela, e não a que abriu a janela: depois de navegar, é esta
  //que o "abrir em página inteira" tem de mostrar
  get fullPageRoute() {
    return instanceRoute(this.current)
  }
}

export default toNative(ViewInstanceDialog)
</script>
